import { z } from 'zod';
import { ApprovalDecisionValue, ApprovalRequest, ApprovalRequestStatus } from './approval.ts';
import { Case, CaseLink, CaseState, CaseTimelineEntry } from './case.ts';
import {
  EntityRef,
  Id,
  IdempotencyKey,
  IsoDateTime,
  JsonObject,
  JsonSchemaObject,
  SemVer,
} from './common.ts';
import { Deadline } from './deadline.ts';
import {
  DeleteDocumentBody,
  Document,
  DocumentListQuery,
  UploadDocumentQuery,
  VerifyDocumentBody,
} from './document.ts';
import { Entitlement, ResourceScope } from './entitlement.ts';
import { PlatformEvent } from './events.ts';
import { EvidenceExport } from './evidence.ts';
import { Hold, HoldRequest, ParkedResult, ResumeRequest } from './hold.ts';
import {
  CancelTaskBody,
  ClarifyTaskBody,
  ClarifyTaskResponse,
  defineRoute,
  PLATFORM_ROLES,
  TaskView,
} from './http.ts';
import { InboundDisposition, InboundRecord } from './inbound.ts';
import {
  CreateObligationBody,
  Obligation,
  ObligationDecisionOption,
  ObligationStatus,
  UpdateObligationBody,
} from './obligation.ts';
import { BusinessOperation } from './operation.ts';
import { ExternalPrincipal, InviteExternalBody, InviteExternalResult } from './principal.ts';
import {
  ActivityInstance,
  ProcessDefinitionMeta,
  ProcessInstance,
  ProcessInstanceState,
  ProcessName,
  ProcessOutcome,
  ReleasePin,
} from './process.ts';
import { ClarificationQuestion } from './proposal.ts';
import { CaseDisplayState, Resolution, ResolutionState } from './resolution.ts';
import { Task, TaskState } from './task.ts';
import { AssignWorkItemBody, WorkItem, WorkItemState, WorkItemView } from './work-item.ts';

/**
 * Added by 17e (operator UX): browser-safe descriptors of the process/case operator surface. The routes are
 * mounted by the owning core modules (`core-cases`, `core-workflows`, `core-approvals`, `core-action-ledger`,
 * `core-intake`), which define them locally; these descriptors mirror them (same id, method, path, request and
 * response shapes) so the workbench and pack UIs call them through `ApiClient.call` without importing core. They
 * are not part of `api` / `ALL_ROUTES`; since 20a `scripts/export-contracts.ts` adds them to `generated/openapi.json`
 * and core-cases / core-intake mount the case descriptors themselves. A drift test in
 * `apps/platform/src/operator-routes.test.ts` compares both sides (method, path, roles and the JSON Schema of the
 * request and response parts).
 */

const OPERATOR = [PLATFORM_ROLES.operator, PLATFORM_ROLES.admin] as const;

/** A process instance as the case and instance routes return it (`pin` optional for rows recorded before 17b2). */
export const ProcessInstanceView = ProcessInstance.extend({ pin: ReleasePin.optional() });
export type ProcessInstanceView = z.infer<typeof ProcessInstanceView>;

/** Added by 20a: name of the platform one-activity process (`POST /cases` with `agent` starts it). */
export const AGENT_RUN_PROCESS = 'platform.agent-run';
/** Added by 20a: its only version. */
export const AGENT_RUN_PROCESS_VERSION = '1.0.0';
/** Added by 20a: its only activity (kind `agent`), activation `run#1`. */
export const AGENT_RUN_ACTIVITY = 'run';

/**
 * Added by 20a: `POST /cases`. Exactly one of `process` (start that process; `scope` and `inputs` are its start
 * scope and input) or `agent` (a one-activity case: process `platform.agent-run` with one `agent` activity running
 * the capability; `objective` required, `scope.domain` defaults to the serving agent's domain). `idempotencyKey`:
 * same key and same body → the same case; same key with another body → 409 `idempotency_conflict`.
 */
export const StartCaseBody = z
  .object({
    process: z.object({ name: ProcessName, version: SemVer.optional() }).optional(),
    agent: z
      .object({ capability: z.string().min(1).max(200), version: z.string().min(1).max(50) })
      .optional(),
    title: z.string().min(1).max(300).optional(),
    objective: z.string().min(1).max(4000).optional(),
    inputs: JsonObject.optional(),
    entityRefs: z.array(EntityRef).optional(),
    artifactRefs: z.array(z.string().min(1)).optional(),
    deadline: IsoDateTime.optional(),
    requestedActionScope: z.array(z.string().min(1)).optional(),
    scope: z.record(z.string(), z.string()).optional(),
    idempotencyKey: IdempotencyKey.optional(),
    /** Where the request came from (`workbench`, `api`, `demo`, `evals`…); default `api`. Agent starts only. */
    channel: z.string().min(1).max(64).optional(),
  })
  .superRefine((b, ctx) => {
    if ((b.process === undefined) === (b.agent === undefined)) {
      ctx.addIssue({ code: 'custom', message: 'set exactly one of process or agent', path: ['process'] });
    }
    if (b.agent !== undefined && b.objective === undefined) {
      ctx.addIssue({ code: 'custom', message: 'an agent start needs an objective', path: ['objective'] });
    }
  })
  .meta({ id: 'StartCaseBody' });
export type StartCaseBody = z.infer<typeof StartCaseBody>;
export type StartCaseBodyInput = z.input<typeof StartCaseBody>;

/** Added by 20a: answer of `POST /cases` (`activationId`/`taskId` for agent starts). */
export const StartCaseResponse = z
  .object({
    caseId: Id,
    processInstanceId: Id,
    activationId: z.string().optional(),
    taskId: Id.optional(),
  })
  .meta({ id: 'StartCaseResponse' });
export type StartCaseResponse = z.infer<typeof StartCaseResponse>;

/** Added by 20a: what an `agent` activity waits for while its task is `awaiting_input` / `awaiting_approval`. */
export const ActivityWaiting = z
  .object({
    kind: z.enum(['input', 'approval']),
    /** `input`: the open clarification questions (answers keyed by `id`). */
    questions: z.array(ClarificationQuestion).optional(),
    /** `input`: the task timeline entry that asked them (one answer per request). */
    entryId: Id.optional(),
    /** `approval`: the pending task approval (`/approvals/{approvalId}`). */
    approvalId: Id.optional(),
  })
  .meta({ id: 'ActivityWaiting' });
export type ActivityWaiting = z.infer<typeof ActivityWaiting>;

/**
 * Added by 20a: an activity as the case detail returns it. For kind `agent`: `taskId`, `taskState` (the episode's
 * task state), `waiting`, `outcome` and, when asked (`?include=episode`, or the activity selected with
 * `?activity=<activationId>`), `episode` — the same view `GET /tasks/{taskId}` returns, ACL-redacted for the viewer.
 */
export const CaseActivityView = ActivityInstance.safeExtend({
  taskState: TaskState.optional(),
  waiting: ActivityWaiting.optional(),
  episode: TaskView.optional(),
}).meta({ id: 'CaseActivityView' });
export type CaseActivityView = z.infer<typeof CaseActivityView>;

/** Added by 20a: query of `GET /cases/{caseId}`. */
export const CaseDetailQuery = z.object({
  include: z.literal('episode').optional(),
  activity: z.string().min(1).max(200).optional(),
});

/** Added by 28: at most this many holders are named per role. */
export const ROLE_HOLDERS_CAP = 8;

/**
 * Added by 28: one directory user who holds a role (`CaseDetailView.roleHolders`). Only the display name is shared
 * (the server falls back to the username when the display name is blank).
 */
export const RoleHolder = z.object({ displayName: z.string().min(1) }).meta({ id: 'RoleHolder' });
export type RoleHolder = z.infer<typeof RoleHolder>;

/**
 * Added by 28: the enabled directory users who hold a role, capped at `ROLE_HOLDERS_CAP`; `total` counts all the
 * server read. `partial: true` when the server did not read the whole directory (`total` is then a lower bound and
 * `total === 0` does not mean nobody holds the role).
 */
export const RoleHolders = z
  .object({
    holders: z.array(RoleHolder).max(ROLE_HOLDERS_CAP),
    total: z.number().int().min(0),
    partial: z.literal(true).optional(),
  })
  .meta({ id: 'RoleHolders' });
export type RoleHolders = z.infer<typeof RoleHolders>;
/**
 * Added by 26a (brief 26 §3.3): an obligation as the viewer sees it — overdue flag, the viewer's relation to it and
 * what the viewer may do. `answerSchema` is the JSON Schema of the answer form (absent = `DefaultObligationAnswer`).
 */
export const ObligationView = Obligation.safeExtend({
  overdue: z.boolean(),
  viewerIs: z.array(z.enum(['owner', 'assignee', 'respondent'])).default([]),
  canAnswer: z.boolean(),
  canWaive: z.boolean(),
  answerSchema: JsonSchemaObject.optional(),
}).meta({ id: 'ObligationView' });
export type ObligationView = z.infer<typeof ObligationView>;

/** Added by 26a (brief 26 §3.3): the planned next run of a case and the obligations it waits on. */
export const CaseContinuationView = z
  .object({
    nextActivationId: z.string().min(1).max(200),
    waitingOn: z.array(Id).default([]),
    runsUsed: z.number().int().nonnegative(),
    maxContinuations: z.number().int().nonnegative(),
  })
  .meta({ id: 'CaseContinuationView' });
export type CaseContinuationView = z.infer<typeof CaseContinuationView>;

/** `GET /cases/{caseId}`. */
export const CaseDetailView = z
  .object({
    case: Case,
    processInstances: z.array(ProcessInstanceView),
    activities: z.array(CaseActivityView),
    operations: z.array(BusinessOperation),
    approvalRequests: z.array(ApprovalRequest),
    /** Added by 21 step 0 (§1b.3): the open hold (reason, owner role, resume target, evidence required). */
    hold: Hold.optional(),
    /** Added by 21 step 0 (§1b.3): results parked while held, delivered to the handler after resume. */
    parked: z.array(ParkedResult).optional(),
    /** Added by 21 step 0 (§1b.6): state of the case's main process instance (completion ≠ closure). */
    processState: ProcessInstanceState.optional(),
    /** Added by 21 step 0 (§1b.6): the process succeeded but open obligations keep the case `in_progress`. */
    awaitingClosure: z.boolean().optional(),
    /** Added by 21 step 0: obligations of the case (open ones stay on "Waiting on you" after closure). */
    obligations: z.array(Obligation).optional(),
    /** Added by 21 step 0: documents of the case the viewer may read (ACL-filtered server-side). */
    documents: z.array(Document).optional(),
    /** Added by 21 step 0: the next human step, shown at the top of the case. */
    nextAction: z
      .object({
        label: z.string().min(1).max(300),
        ownerRole: z.string().min(1),
        deadline: Deadline.optional(),
        activationId: z.string().min(1).max(200),
      })
      .optional(),
    /** Added by 21 step 0 (§1b.7): configuration version snapshotted at start (`null` for legacy instances). */
    configVersion: z.string().min(1).nullable().optional(),
    /**
     * Added by 28: directory users who hold the owner roles of the open human steps (nextAction and open work
     * items). Keyed by role. Absent when no UserDirectory is bound.
     */
    roleHolders: z.record(z.string(), RoleHolders).optional(),
    /** Added by 26a (brief 26 §3.3): the evaluated resolution (condition, state, explanation, blocking obligations). */
    resolution: Resolution.optional(),
    /** Added by 26a: the next continuation run and what it waits on (absent when none is planned). */
    continuation: CaseContinuationView.optional(),
    /** Added by 26a: `obligations` as the viewer sees them (old clients keep reading `obligations`). */
    obligationViews: z.array(ObligationView).optional(),
  })
  .describe('Case detail (GET /cases/{caseId})');
export type CaseDetailView = z.infer<typeof CaseDetailView>;

/** One registered process definition version (`GET /processes/definitions`). */
export const ProcessDefinitionView = z
  .object({
    /** `name@version`. */
    key: z.string().min(1),
    /** Module (pack) that registered it. */
    module: z.string().min(1),
    /** True when this deployment runs its workflow version (new starts may use it). */
    deployed: z.boolean(),
    meta: ProcessDefinitionMeta,
  })
  .meta({ id: 'ProcessDefinitionView' });
export type ProcessDefinitionView = z.infer<typeof ProcessDefinitionView>;

/**
 * Added by 21 step 0: `GET /processes/{name}` — one process definition (newest deployed version, or `?version=`)
 * with its served start-input schema (`inputSchemaRef` → JSON Schema with `x-classification` / `x-ui`).
 */
export const ProcessDefinitionDetailView = ProcessDefinitionView.extend({
  inputSchema: JsonSchemaObject.optional(),
}).meta({ id: 'ProcessDefinitionDetailView' });
export type ProcessDefinitionDetailView = z.infer<typeof ProcessDefinitionDetailView>;

/**
 * Added by 29: the first human step of a process as the caller would meet it at start: the first declared `human`
 * activity with an `ownerRole` (meta order). `viewerHolds` says whether the caller holds that role; `holders` names
 * who does (only when the caller does not hold it and a UserDirectory answered).
 */
export const StartPreflightStep = z
  .object({
    activityId: z.string().min(1),
    /** The activity title (its id when it has none). */
    title: z.string().min(1).max(300),
    ownerRole: z.string().min(1),
    viewerHolds: z.boolean(),
    holders: RoleHolders.optional(),
  })
  .meta({ id: 'StartPreflightStep' });
export type StartPreflightStep = z.infer<typeof StartPreflightStep>;

/**
 * Added by 29: `GET /processes/definitions/{key}/start-preflight` — read-only facts the start form shows before
 * submit. `firstHumanStep` is absent when the process declares no human activity with an owner role.
 */
export const StartPreflightView = z
  .object({ firstHumanStep: StartPreflightStep.optional() })
  .meta({ id: 'StartPreflightView' });
export type StartPreflightView = z.infer<typeof StartPreflightView>;

export const CaseListQuery = z.object({
  state: CaseState.optional(),
  /** Exact scope filter `dimension:value[,dimension:value]`; entitlements still apply per case. */
  scope: z
    .string()
    .regex(/^[a-z][a-zA-Z0-9]*:[^,:]+(,[a-z][a-zA-Z0-9]*:[^,:]+)*$/, 'scope must be dimension:value[,…]')
    .optional(),
  processType: z.string().optional(),
  limit: z.coerce.number().int().positive().max(200).optional(),
  /**
   * Added by 20a2: `me` = only cases with something waiting on the caller (an agent activity awaiting the caller's
   * input or approval, a pending approval request stage the caller may decide now, an open work item of a role the
   * caller holds or one the caller claimed). Entitlements still apply per case.
   */
  attention: z.enum(['me']).optional(),
  /** Added by 20a2: `me` = only cases the caller created. */
  createdBy: z.enum(['me']).optional(),
  /** Added by 20a2: case-insensitive text search on the title and the business keys. */
  q: z.string().trim().min(1).max(200).optional(),
  /** Added by 20a2: cases whose scope dimension `domain` equals this value. */
  domain: z.string().min(1).max(200).optional(),
  /** Added by 20a2: opaque cursor from a previous page's `nextCursor` (order: newest created first, then id). */
  cursor: z.string().min(1).max(500).optional(),
});
export type CaseListQuery = z.infer<typeof CaseListQuery>;

/** Response of `GET /cases` (named by 20a2; `nextCursor` and `waitingOnMe` added by 20a2). */
export const CaseListResponse = z
  .object({
    items: z.array(Case),
    /** Present when another page exists: pass it as `cursor`. */
    nextCursor: z.string().optional(),
    /** Ids of the returned cases with something waiting on the caller (the `attention=me` rule). */
    waitingOnMe: z.array(Id).optional(),
  })
  .meta({ id: 'CaseListResponse' });
export type CaseListResponse = z.infer<typeof CaseListResponse>;

const PendingEffect = z.object({
  taskId: z.string().optional(),
  operationId: z.string().optional(),
  reason: z.string(),
});

export const CaseCancelResponse = z.object({
  caseId: Id,
  case: Case,
  processes: z.array(
    z.object({
      processInstanceId: Id,
      state: z.string(),
      outcome: z.string().optional(),
      pending: z.array(PendingEffect),
    }),
  ),
});

export const ProcessCancelOutcome = z.object({
  processInstanceId: Id,
  state: ProcessInstanceState,
  outcome: ProcessOutcome.optional(),
  pending: z.array(PendingEffect),
});

const Reason = z.object({ reason: z.string().min(1).max(2000) });
const ActivityParams = z.object({ caseId: Id, activationId: z.string().min(1).max(200) });

/** Added by 21 step 0, mounted by 21a (core-cases): `POST /cases/{caseId}/hold`. */
const holdCaseRoute = defineRoute({
  id: 'cases.hold',
  method: 'post',
  path: '/cases/{caseId}/hold',
  summary:
    'Put the case’s process on a boundary hold (no new spawns or waits; results park; deadlines keep running)',
  tags: ['cases'],
  request: { params: z.object({ caseId: Id }), body: HoldRequest },
  response: CaseDetailView,
});

/** Added by 21 step 0, mounted by 21a (core-cases): `POST /cases/{caseId}/resume`. */
const resumeCaseRoute = defineRoute({
  id: 'cases.resume',
  method: 'post',
  path: '/cases/{caseId}/resume',
  summary: 'Resume a held case with evidence (hold owner role); parked results reach the handler',
  tags: ['cases'],
  request: { params: z.object({ caseId: Id }), body: ResumeRequest },
  response: CaseDetailView,
});

/** `GET|POST /cases…`, `/entitlements…` (core-cases). */
/** Added by 26b (brief 26 §5.4): what `POST /cases/rebuild/{id}` did to the case's outcome. */
export const CaseRebuildOutcome = z
  .object({
    resolution: ResolutionState.optional(),
    display: CaseDisplayState.optional(),
    applied: z.object({
      timeline: z.number().int().nonnegative(),
      resolution: z.number().int().nonnegative(),
      state: z.number().int().nonnegative(),
      display: z.number().int().nonnegative(),
    }),
  })
  .meta({ id: 'CaseRebuildOutcome' });
export type CaseRebuildOutcome = z.infer<typeof CaseRebuildOutcome>;

export const caseOperatorRoutes = {
  /** Added by 21a (moved from `wave21OperatorRoutes.holdCase`). */
  hold: holdCaseRoute,
  /** Added by 21a (moved from `wave21OperatorRoutes.resumeCase`). */
  resume: resumeCaseRoute,
  list: defineRoute({
    id: 'cases.list',
    method: 'get',
    path: '/cases',
    summary: 'Cases the caller is entitled to view',
    tags: ['cases'],
    request: { query: CaseListQuery },
    response: CaseListResponse,
  }),
  get: defineRoute({
    id: 'cases.get',
    method: 'get',
    path: '/cases/{caseId}',
    summary: 'Case with process instances, activities, operations and approval requests',
    tags: ['cases'],
    request: { params: z.object({ caseId: Id }), query: CaseDetailQuery },
    response: CaseDetailView,
  }),
  /** Added by 20a (served by core-intake): start a process case or a one-activity agent case. */
  start: defineRoute({
    id: 'cases.start',
    method: 'post',
    path: '/cases',
    summary:
      'Start a case: a process (`process`) or a one-activity agent case (`agent`, process platform.agent-run); idempotent by idempotencyKey',
    tags: ['cases'],
    request: { body: StartCaseBody },
    response: StartCaseResponse,
    successStatus: 201,
  }),
  /** Added by 20a: live case events (replaces polling). */
  events: defineRoute({
    id: 'cases.events',
    method: 'get',
    path: '/cases/{caseId}/events',
    summary:
      'Server-sent events of one case (event = type from CASE_SSE_EVENT_TYPES, data = PlatformEvent); replays the timeline on connect',
    tags: ['cases'],
    request: { params: z.object({ caseId: Id }) },
    response: PlatformEvent,
    responseType: 'sse',
  }),
  /** Added by 20a (served by core-intake): answer the clarification questions of an agent activity. */
  clarifyActivity: defineRoute({
    id: 'cases.activities.clarify',
    method: 'post',
    path: '/cases/{caseId}/activities/{activationId}/clarify',
    summary: 'Answer the clarification questions of an agent activity (case creator or act entitlement)',
    tags: ['cases'],
    request: { params: ActivityParams, body: ClarifyTaskBody },
    response: ClarifyTaskResponse,
  }),
  /** Added by 20a (served by core-intake): cancel an agent activity (a one-activity case cancels its process). */
  cancelActivity: defineRoute({
    id: 'cases.activities.cancel',
    method: 'post',
    path: '/cases/{caseId}/activities/{activationId}/cancel',
    summary: 'Cancel an agent activity (case creator or act entitlement)',
    tags: ['cases'],
    request: { params: ActivityParams, body: CancelTaskBody },
    response: Task,
  }),
  timeline: defineRoute({
    id: 'cases.timeline',
    method: 'get',
    path: '/cases/{caseId}/timeline',
    summary: 'Case timeline (entries filtered by entry scope and ACL)',
    tags: ['cases'],
    request: { params: z.object({ caseId: Id }) },
    response: z.object({ items: z.array(CaseTimelineEntry) }),
  }),
  artefacts: defineRoute({
    id: 'cases.artefacts',
    method: 'get',
    path: '/cases/{caseId}/artefacts',
    summary: 'Typed links of the case',
    tags: ['cases'],
    request: { params: z.object({ caseId: Id }) },
    response: z.object({ items: z.array(CaseLink) }),
  }),
  cancel: defineRoute({
    id: 'cases.cancel',
    method: 'post',
    path: '/cases/{caseId}/cancel',
    summary: 'Cancel the case’s processes (stays cancelling while effects are unresolved)',
    tags: ['cases'],
    request: { params: z.object({ caseId: Id }), body: Reason },
    response: CaseCancelResponse,
  }),
  rebuild: defineRoute({
    id: 'cases.rebuild',
    method: 'post',
    path: '/cases/rebuild/{processInstanceId}',
    summary: 'Rebuild the case projection of a process instance from the journal (operator)',
    tags: ['cases'],
    roles: OPERATOR,
    request: { params: z.object({ processInstanceId: Id }) },
    response: z.object({
      case: Case,
      applied: z.number().int(),
      skipped: z.number().int(),
      /** Added by 26b (brief 26 §5.4): the rebuilt outcome and the writes per part (each also in `applied`). */
      outcome: CaseRebuildOutcome.optional(),
    }),
  }),
  entitlements: defineRoute({
    id: 'entitlements.mine',
    method: 'get',
    path: '/entitlements/me',
    summary: 'The caller’s entitlements',
    tags: ['cases'],
    request: {},
    response: z.object({ items: z.array(Entitlement) }),
  }),
  revoke: defineRoute({
    id: 'entitlements.revoke',
    method: 'post',
    path: '/entitlements/revoke',
    summary: 'Revoke a principal’s entitlements (pending votes are invalidated)',
    tags: ['cases'],
    roles: OPERATOR,
    request: { body: z.object({ principalId: z.string().min(1), dimension: z.string().min(1).optional() }) },
    response: z.object({ revoked: z.number().int() }),
  }),
} as const;

/** `/processes…` (core-workflows). */
/** Added by 21 step 0, mounted by 21a (core-workflows): `GET /processes/{name}` with the start-input schema. */
const processDefinitionRoute = defineRoute({
  id: 'processes.get',
  method: 'get',
  path: '/processes/{name}',
  summary: 'One process definition (newest deployed, or ?version=) with its start-input JSON Schema',
  tags: ['processes'],
  request: {
    params: z.object({ name: z.string().min(1) }),
    query: z.object({ version: SemVer.optional() }),
  },
  response: ProcessDefinitionDetailView,
});

export const processOperatorRoutes = {
  /** Added by 21a (moved from `wave21OperatorRoutes.processDefinition`). */
  definition: processDefinitionRoute,
  definitions: defineRoute({
    id: 'processes.definitions.list',
    method: 'get',
    path: '/processes/definitions',
    summary: 'Registered process definition versions (metadata only)',
    tags: ['processes'],
    request: {},
    response: z.object({ items: z.array(ProcessDefinitionView) }),
  }),
  /**
   * Added by 29 (mounted by core-cases): the first human step of `key` (`name@version`) and whether the caller holds
   * its owner role. Read-only; the start itself is never blocked by it.
   */
  startPreflight: defineRoute({
    id: 'processes.startPreflight',
    method: 'get',
    path: '/processes/definitions/{key}/start-preflight',
    summary: 'Before a start: the first human step, whether the caller holds its role, and who does',
    tags: ['processes'],
    request: { params: z.object({ key: z.string().min(3).max(300) }) },
    response: StartPreflightView,
  }),
  start: defineRoute({
    id: 'processes.start',
    method: 'post',
    path: '/processes/{name}/start',
    summary: 'Start a process: creates the case and the run (Idempotency-Key header required)',
    tags: ['processes'],
    request: {
      params: z.object({ name: z.string().min(1) }),
      headers: z.object({ 'idempotency-key': z.string().min(8).max(200) }),
      body: z.object({
        version: z.string().min(1).optional(),
        scope: ResourceScope,
        title: z.string().min(1).max(300).optional(),
        businessKeys: z.array(z.string().min(1).max(255)).max(50).optional(),
        input: z.record(z.string(), z.unknown()).optional(),
      }),
    },
    response: z.object({ processInstance: ProcessInstanceView, caseId: Id.optional(), created: z.boolean() }),
    successStatus: 201,
  }),
  get: defineRoute({
    id: 'processes.instances.get',
    method: 'get',
    path: '/processes/instances/{processInstanceId}',
    summary: 'Process instance with its activities',
    tags: ['processes'],
    request: { params: z.object({ processInstanceId: Id }) },
    response: z.object({ processInstance: ProcessInstanceView, activities: z.array(ActivityInstance) }),
  }),
  cancel: defineRoute({
    id: 'processes.instances.cancel',
    method: 'post',
    path: '/processes/instances/{processInstanceId}/cancel',
    summary: 'Cancel a process instance (stays cancelling while effects are unresolved)',
    tags: ['processes'],
    request: { params: z.object({ processInstanceId: Id }), body: Reason },
    response: ProcessCancelOutcome,
  }),
} as const;

/** `/approval-requests…` (core-approvals). */
export const approvalRequestOperatorRoutes = {
  list: defineRoute({
    id: 'approvalRequests.list',
    method: 'get',
    path: '/approval-requests',
    summary: 'List approval requests visible to the principal',
    tags: ['approvals'],
    request: {
      query: z.object({
        caseId: Id.optional(),
        processInstanceId: Id.optional(),
        status: ApprovalRequestStatus.optional(),
        /**
         * Added by 17e review (S3): `me` keeps only pending requests the caller may decide now — an open stage whose
         * required role (or active `add_decider` escalation role) the caller holds, `decide` on the stage scope, and
         * no SoD bar. Server-computed; the workbench "Mine" filter uses it.
         */
        decidableBy: z.literal('me').optional(),
      }),
    },
    response: z.object({ items: z.array(ApprovalRequest) }),
  }),
  get: defineRoute({
    id: 'approvalRequests.get',
    method: 'get',
    path: '/approval-requests/{requestId}',
    summary: 'One approval request revision',
    tags: ['approvals'],
    request: { params: z.object({ requestId: Id }) },
    response: ApprovalRequest,
  }),
  decide: defineRoute({
    id: 'approvalRequests.decide',
    method: 'post',
    path: '/approval-requests/{requestId}/decisions',
    summary: 'Record one stage decision (approve, reject or return)',
    tags: ['approvals'],
    request: {
      params: z.object({ requestId: Id }),
      body: z.object({
        stageId: z.string().min(1),
        decision: ApprovalDecisionValue,
        rationale: z.string().min(1).max(4000),
        revision: z.number().int().positive().optional(),
      }),
    },
    response: ApprovalRequest,
  }),
} as const;

const WorkItemParams = z.object({ workItemId: Id });

/** 21b: mounted by core-workflows; listed in `operatorApi.workItems` and (unchanged) in `wave21OperatorRoutes`. */
const assignWorkItemRoute = defineRoute({
  id: 'workItems.assign',
  method: 'post',
  path: '/work-items/{workItemId}/assign',
  summary: 'Assign (or clear) the principal a work item is narrowed to; never grants approval authority',
  tags: ['work-items'],
  request: { params: z.object({ workItemId: Id }), body: AssignWorkItemBody },
  response: WorkItem,
});

/** 21b: mounted by core-workflows (`GET /me/work-items`, also under an external invitation token). */
const myWorkItemsRoute = defineRoute({
  id: 'me.workItems',
  method: 'get',
  path: '/me/work-items',
  summary: 'Work items assigned to the caller (works under an external invitation token)',
  tags: ['work-items'],
  request: { query: z.object({ state: WorkItemState.optional() }) },
  response: z.object({ items: z.array(WorkItemView) }),
});

/** `/work-items…` (core-workflows, 17c1 D3). */
export const workItemOperatorRoutes = {
  list: defineRoute({
    id: 'workItems.list',
    method: 'get',
    path: '/work-items',
    summary: 'Work items the caller may view (filter by case, instance, state)',
    tags: ['work-items'],
    request: {
      query: z.object({
        caseId: Id.optional(),
        processInstanceId: Id.optional(),
        state: WorkItemState.optional(),
        limit: z.coerce.number().int().min(1).max(500).optional(),
      }),
    },
    response: z.object({ items: z.array(WorkItem) }),
  }),
  get: defineRoute({
    id: 'workItems.get',
    method: 'get',
    path: '/work-items/{workItemId}',
    summary: 'One work item with its served output schema',
    tags: ['work-items'],
    request: { params: WorkItemParams },
    // 21b: `WorkItemView` (the item plus `outputSchema` from the pack's registered Zod schema).
    response: WorkItemView,
  }),
  claim: defineRoute({
    id: 'workItems.claim',
    method: 'post',
    path: '/work-items/{workItemId}/claim',
    summary: 'Claim a work item (owner role and act entitlement on its scope)',
    tags: ['work-items'],
    request: { params: WorkItemParams },
    response: WorkItem,
  }),
  complete: defineRoute({
    id: 'workItems.complete',
    method: 'post',
    path: '/work-items/{workItemId}/complete',
    summary: 'Complete a work item with its typed output; signals the process run',
    tags: ['work-items'],
    request: { params: WorkItemParams, body: z.object({ output: z.record(z.string(), z.unknown()) }) },
    response: WorkItem,
  }),
  cancel: defineRoute({
    id: 'workItems.cancel',
    method: 'post',
    path: '/work-items/{workItemId}/cancel',
    summary: 'Cancel an open work item (owner role or operator, act entitlement)',
    tags: ['work-items'],
    request: { params: WorkItemParams, body: Reason },
    response: WorkItem,
  }),
  /** 21b: mounted (`wave21OperatorRoutes.assignWorkItem`). */
  assign: assignWorkItemRoute,
  /** 21b: mounted (`wave21OperatorRoutes.myWorkItems`). */
  mine: myWorkItemsRoute,
} as const;

const OperationParams = z.object({ operationId: Id });

/** `/operations…` (core-action-ledger, ADR-017). */
export const operationOperatorRoutes = {
  list: defineRoute({
    id: 'operations.list',
    method: 'get',
    path: '/operations',
    summary: 'Business operations of the tenant, optionally of one case',
    tags: ['operations'],
    request: { query: z.object({ caseId: Id.optional() }) },
    response: z.object({ items: z.array(BusinessOperation) }),
  }),
  get: defineRoute({
    id: 'operations.get',
    method: 'get',
    path: '/operations/{operationId}',
    summary: 'One business operation',
    tags: ['operations'],
    request: { params: OperationParams },
    response: BusinessOperation,
  }),
  release: defineRoute({
    id: 'operations.release',
    method: 'post',
    path: '/operations/{operationId}/release',
    summary: 'Release a succeeded or failed business operation (explicit, evidenced reversal)',
    tags: ['operations'],
    roles: OPERATOR,
    request: {
      params: OperationParams,
      body: z.object({ reason: z.string().min(1).max(2000), reversalRef: z.string().min(1) }),
    },
    response: BusinessOperation,
  }),
  reconcile: defineRoute({
    id: 'operations.reconcile',
    method: 'post',
    path: '/operations/{operationId}/reconcile',
    summary: "Reconcile the claiming attempt of a business operation with the tool's reconcile handler",
    tags: ['operations'],
    roles: OPERATOR,
    request: { params: OperationParams },
    response: BusinessOperation,
  }),
} as const;

/** Operator routes of the durable inbox (core-intake, ADR-018). */
export const inboundOperatorRoutes = {
  list: defineRoute({
    id: 'inbound.records.list',
    method: 'get',
    path: '/inbound/records',
    summary: 'List inbox records of the session tenant',
    tags: ['inbound'],
    request: {
      query: z.object({
        disposition: InboundDisposition.optional(),
        /**
         * Added by 17e review (S2): records of one case — correlated to it (`correlation.caseId`), delivered to a run
         * of one of its process instances, or whose trigger start opened it.
         */
        caseId: Id.optional(),
        source: z.string().min(1).optional(),
        type: z.string().min(1).optional(),
        limit: z.coerce.number().int().positive().max(500).optional(),
      }),
    },
    response: z.object({ items: z.array(InboundRecord) }),
    roles: OPERATOR,
  }),
  get: defineRoute({
    id: 'inbound.records.get',
    method: 'get',
    path: '/inbound/records/{inboxId}',
    summary: 'One inbox record',
    tags: ['inbound'],
    request: { params: z.object({ inboxId: Id }) },
    response: InboundRecord,
    roles: OPERATOR,
  }),
  replay: defineRoute({
    id: 'inbound.records.replay',
    method: 'post',
    path: '/inbound/records/{inboxId}/replay',
    summary: 'Operator replay of a stored event (audited; poison rows included)',
    tags: ['inbound'],
    request: { params: z.object({ inboxId: Id }), body: Reason },
    response: z.object({ record: InboundRecord, disposition: InboundDisposition }),
    roles: OPERATOR,
  }),
} as const;

// Wave 21 groups mounted by 21c (moved out of `wave21OperatorRoutes`, which keeps the same objects).
const CaseParams = z.object({ caseId: Id });
const DocumentParams = z.object({ documentId: Id });
const ObligationParams = z.object({ caseId: Id, obligationId: Id });

/** Added by 21c (brief 21 §5 item 1): `/cases/{caseId}/documents`, `/documents/{documentId}…` (core-documents). */
export const documentOperatorRoutes = {
  listDocuments: defineRoute({
    id: 'cases.documents.list',
    method: 'get',
    path: '/cases/{caseId}/documents',
    summary: 'Documents of the case the caller may read (ACL-filtered server-side)',
    tags: ['documents'],
    request: { params: CaseParams, query: DocumentListQuery },
    response: z.object({ items: z.array(Document) }),
  }),
  uploadDocument: defineRoute({
    id: 'cases.documents.upload',
    method: 'post',
    path: '/cases/{caseId}/documents',
    summary:
      'Upload a case document (raw body, Content-Type = file type, metadata in the query); scanned, registered, ACL set',
    tags: ['documents'],
    request: { params: CaseParams, query: UploadDocumentQuery },
    response: Document,
    successStatus: 201,
  }),
  documentContent: defineRoute({
    id: 'documents.content',
    method: 'get',
    path: '/documents/{documentId}/content',
    summary: 'Document bytes (streamed, or 302 to a presigned URL); same ACL as the register row',
    tags: ['documents'],
    request: { params: DocumentParams },
    response: z.string(),
    responseType: 'text',
  }),
  verifyDocument: defineRoute({
    id: 'documents.verify',
    method: 'post',
    path: '/documents/{documentId}/verify',
    summary:
      'Record a verification (method, outcome) on a document; signature proof is e-signature or wet-ink',
    tags: ['documents'],
    request: { params: DocumentParams, body: VerifyDocumentBody },
    response: Document,
  }),
  /** Added by document-delete: retire with the bytes removed (the register row stays, `status: 'retired'`). */
  deleteDocument: defineRoute({
    id: 'documents.delete',
    method: 'delete',
    path: '/documents/{documentId}',
    summary:
      'Retire a document and remove its bytes (uploader or `document.delete.any`); the register row stays',
    tags: ['documents'],
    request: { params: DocumentParams, body: DeleteDocumentBody },
    response: Document,
  }),
} as const;

// -----------------------------------------------------------------------------------------------
// Outcome-driven cases (added by 26a, brief 26 §3.5)
// -----------------------------------------------------------------------------------------------

/** Body of `POST /cases/{caseId}/obligations/{obligationId}/answer` (validated against the answer schema). */
export const AnswerObligationBody = z
  .object({
    answer: JsonObject,
    idempotencyKey: IdempotencyKey,
    /** `eventId` of the answer this one replaces; without it an answer to a met obligation is `late`. */
    revises: z.string().min(1).max(255).optional(),
    /** Only the owner: the answer is recorded for this respondent (external id, and how it was obtained). */
    onBehalfOf: z
      .object({ externalId: z.string().min(1).max(200).optional(), note: z.string().min(1).max(500) })
      .optional(),
  })
  .meta({ id: 'AnswerObligationBody' });
export type AnswerObligationBody = z.infer<typeof AnswerObligationBody>;

/** Response of `POST …/answer`: the obligation, the inbox row and how it was disposed (`delivered|duplicate|late`). */
export const AnswerObligationResponse = z
  .object({
    obligation: Obligation,
    inboxId: Id,
    disposition: InboundDisposition,
    /**
     * Added by 26b review round (brief 26 §5.2): a changed answer on a closed case went to this open follow-up case
     * (the closed case's obligation keeps its answer).
     */
    followUpCaseId: Id.optional(),
  })
  .meta({ id: 'AnswerObligationResponse' });
export type AnswerObligationResponse = z.infer<typeof AnswerObligationResponse>;

/** Body of `POST /cases/{caseId}/obligations/{obligationId}/waive` (owner or `admin`). */
export const WaiveObligationBody = z
  .object({ reason: z.string().min(1).max(2000) })
  .meta({ id: 'WaiveObligationBody' });
export type WaiveObligationBody = z.infer<typeof WaiveObligationBody>;

/** Body of `POST /cases/{caseId}/obligations/{obligationId}/decide` (`decision_needed` only). */
export const DecideObligationBody = z
  .object({ decision: ObligationDecisionOption, reason: z.string().min(1).max(2000) })
  .meta({ id: 'DecideObligationBody' });
export type DecideObligationBody = z.infer<typeof DecideObligationBody>;

/**
 * Added by 26a phase 4 (§4.5): body of `POST /cases/{caseId}/obligations/{obligationId}/ask-again` (owner only): the
 * ask is sent again with idempotency key `obligation-ask:<id>:<n>` (n = asks so far + 1); `note` goes on the timeline.
 */
export const AskAgainObligationBody = z
  .object({ note: z.string().min(1).max(500).optional() })
  .meta({ id: 'AskAgainObligationBody' });
export type AskAgainObligationBody = z.infer<typeof AskAgainObligationBody>;

/** Query of `GET /me/obligations`: the viewer's obligations (owner role, assignee, respondent). */
export const MyObligationsQuery = z.object({
  state: ObligationStatus.default('open'),
  limit: z.coerce.number().int().positive().max(200).optional(),
  cursor: z.string().min(1).max(500).optional(),
});
export type MyObligationsQuery = z.infer<typeof MyObligationsQuery>;

/** Response of `GET /me/obligations`: overdue first, then by due date. */
export const MyObligationsResponse = z
  .object({ items: z.array(ObligationView), nextCursor: z.string().optional() })
  .meta({ id: 'MyObligationsResponse' });
export type MyObligationsResponse = z.infer<typeof MyObligationsResponse>;

/**
 * Added by 26a (brief 26 §3.5): descriptors of the outcome routes. Contract stubs like `wave21OperatorRoutes`: kept
 * out of `operatorApi` until core-cases mounts them (the lc-06 drift test requires a mounted twin for every
 * `operatorApi` descriptor); `scripts/export-contracts.ts` emits them into `generated/openapi.json`. 26a phase 2 mounted all
 * four in core-cases, so the same descriptor objects are also in `obligationOperatorRoutes` (the export de-duplicates
 * by id).
 */
export const outcomeOperatorRoutes = {
  answerObligation: defineRoute({
    id: 'cases.obligations.answer',
    method: 'post',
    path: '/cases/{caseId}/obligations/{obligationId}/answer',
    summary:
      'Answer an obligation (assignee, owner role, respondent or the external respondent); idempotent by idempotencyKey through the durable inbox',
    tags: ['obligations'],
    request: { params: ObligationParams, body: AnswerObligationBody },
    response: AnswerObligationResponse,
  }),
  waiveObligation: defineRoute({
    id: 'cases.obligations.waive',
    method: 'post',
    path: '/cases/{caseId}/obligations/{obligationId}/waive',
    summary: 'Waive an obligation with a reason (owner or admin)',
    tags: ['obligations'],
    request: { params: ObligationParams, body: WaiveObligationBody },
    response: Obligation,
  }),
  decideObligation: defineRoute({
    id: 'cases.obligations.decide',
    method: 'post',
    path: '/cases/{caseId}/obligations/{obligationId}/decide',
    summary:
      'Decide a decision_needed obligation: retry (next run) or abandon (case stopped with the reason)',
    tags: ['obligations'],
    request: { params: ObligationParams, body: DecideObligationBody },
    response: Obligation,
  }),
  /** Added by 26a phase 4 (§4.5): the owner's "Ask again" (a fresh ask, new idempotency key). */
  askAgainObligation: defineRoute({
    id: 'cases.obligations.askAgain',
    method: 'post',
    path: '/cases/{caseId}/obligations/{obligationId}/ask-again',
    summary:
      'Send the ask of an open obligation again (owner only; a new idempotency key obligation-ask:<id>:<n>)',
    tags: ['obligations'],
    request: { params: ObligationParams, body: AskAgainObligationBody },
    response: Obligation,
  }),
  myObligations: defineRoute({
    id: 'me.obligations',
    method: 'get',
    path: '/me/obligations',
    summary: 'The caller’s obligations (owner role, assignee, respondent), overdue first, then by due date',
    tags: ['obligations'],
    request: { query: MyObligationsQuery },
    response: MyObligationsResponse,
  }),
} as const;

/** Added by 21c (brief 21 §5 items 3, 4): obligations and the evidence export of a case (core-cases). */
export const obligationOperatorRoutes = {
  createObligation: defineRoute({
    id: 'cases.obligations.create',
    method: 'post',
    path: '/cases/{caseId}/obligations',
    summary: 'Open an obligation on the case (owner role, deadline, escalation)',
    tags: ['obligations'],
    request: { params: CaseParams, body: CreateObligationBody },
    response: Obligation,
    successStatus: 201,
  }),
  updateObligation: defineRoute({
    id: 'cases.obligations.update',
    method: 'patch',
    path: '/cases/{caseId}/obligations/{obligationId}',
    summary: 'Mark an obligation met, waive it (reason), reassign it or move an extendable deadline',
    tags: ['obligations'],
    request: { params: ObligationParams, body: UpdateObligationBody },
    response: Obligation,
  }),
  evidenceIndex: defineRoute({
    id: 'cases.evidenceIndex',
    method: 'get',
    path: '/cases/{caseId}/evidence-index',
    summary: 'Canonical evidence export of the case, redacted for the caller, with a sha256 checksum',
    tags: ['cases'],
    request: { params: CaseParams },
    response: EvidenceExport,
  }),
  /** Added by 26a (mounted by core-cases): same descriptor objects as `outcomeOperatorRoutes`. */
  answerObligation: outcomeOperatorRoutes.answerObligation,
  waiveObligation: outcomeOperatorRoutes.waiveObligation,
  decideObligation: outcomeOperatorRoutes.decideObligation,
  myObligations: outcomeOperatorRoutes.myObligations,
  askAgainObligation: outcomeOperatorRoutes.askAgainObligation,
} as const;

/** Every operator descriptor, grouped like `api`. */
export const operatorApi = {
  cases: caseOperatorRoutes,
  processes: processOperatorRoutes,
  approvalRequests: approvalRequestOperatorRoutes,
  workItems: workItemOperatorRoutes,
  operations: operationOperatorRoutes,
  inbound: inboundOperatorRoutes,
  /** Added by 21c. */
  documents: documentOperatorRoutes,
  /** Added by 21c. */
  obligations: obligationOperatorRoutes,
} as const;

// -----------------------------------------------------------------------------------------------
// Wave 21 operator routes (added by 21 step 0, brief 21 §2; ADR-021)
// -----------------------------------------------------------------------------------------------

const ExternalParams = z.object({ externalId: z.string().min(1) });

/**
 * Added by 21 step 0: descriptors of the wave-21 routes. They are **contract stubs**: no core module mounts them
 * yet, so they are kept out of `operatorApi` (the lc-06 drift test requires every `operatorApi` descriptor to have a
 * mounted core twin) and `scripts/export-contracts.ts` emits them into `generated/openapi.json`. When a stream mounts
 * a route it adds the same descriptor object to its `operatorApi` group (additive; the export de-duplicates by id).
 * Owners: hold/resume and `processes.get` 21a; assign, `me.workItems`, externals 21b; documents, obligations and
 * evidence 21c.
 */
export const wave21OperatorRoutes = {
  /** Mounted by 21a: also `operatorApi.cases.hold` (same descriptor object). */
  holdCase: holdCaseRoute,
  /** Mounted by 21a: also `operatorApi.cases.resume` (same descriptor object). */
  resumeCase: resumeCaseRoute,
  listDocuments: documentOperatorRoutes.listDocuments,
  uploadDocument: documentOperatorRoutes.uploadDocument,
  documentContent: documentOperatorRoutes.documentContent,
  verifyDocument: documentOperatorRoutes.verifyDocument,
  deleteDocument: documentOperatorRoutes.deleteDocument,
  createObligation: obligationOperatorRoutes.createObligation,
  updateObligation: obligationOperatorRoutes.updateObligation,
  evidenceIndex: obligationOperatorRoutes.evidenceIndex,
  assignWorkItem: assignWorkItemRoute,
  myWorkItems: myWorkItemsRoute,
  inviteExternal: defineRoute({
    id: 'externals.invite',
    method: 'post',
    path: '/externals',
    summary: 'Invite an external party to named cases (token with expiry; act on every case required)',
    tags: ['externals'],
    request: { body: InviteExternalBody },
    response: InviteExternalResult,
    successStatus: 201,
  }),
  revokeExternal: defineRoute({
    id: 'externals.revoke',
    method: 'post',
    path: '/externals/{externalId}/revoke',
    summary: 'Revoke an external party; its token answers 401 from now on',
    tags: ['externals'],
    request: { params: ExternalParams },
    response: ExternalPrincipal,
  }),
  /** Mounted by 21a: also `operatorApi.processes.definition` (same descriptor object). */
  processDefinition: processDefinitionRoute,
} as const;
